"use strict";
var Promise = require("promise");
var flyd = require("flyd");
var par = require("par");
var pipe = require("fj-pipe");
var filter = require("flyd/module/filter");
var values = require("object-values");
var window = require("global");

var CONSTANTS = require("./constants");
var ACTION_TIMEOUT = 40 * 1000; // 40 seconds

module.exports = get;

function get() {
	var connected = flyd.stream();
	var disconnected = flyd.stream();
	var scanning = flyd.stream();
	var scanned = flyd.stream();
	var events = flyd.stream();
	var errors = flyd.stream();

	var connectedIds = {};
	var pollTimers = {};
	var destroyed = false;

	var api = {
		type: "ble",

		// Streams of useful events
		connected: connected,
		disconnected: disconnected,
		scanning: scanning,
		scanned: scanned,
		events: events,
		errors: errors,

		// General Methods
		on: on,
		destroy: destroy,

		// Bluetooth Methods
		connect: connect,
		disconnect: disconnect,
		scan: scan,
		stopScan: stopScan,
		listen: listen,
		poll: poll,
		read: read,
		write: write,
		unlisten: unlisten,
		unpoll: unpoll,

		// Method generation
		writer: makeWriter,
		reader: makeReader,
		listener: makeListener,
		poller: makePoller
	};

	return new Promise(function (resolve, reject) {
		var resolveAPI = par(resolve, api);

		window.ble.isEnabled(resolveAPI, handleLoadError);

		function handleLoadError(error) {
			if (error === "Class not found")
				return reject();
			window.ble.enable(resolveAPI, reject);
		}
	});

	function destroy() {
		if (destroyed) return;

		var start = Promise.resolve(true);
		if (scanning())
			start = stopScan();

		return start
			.then(disconnectAll)
			.then(clearAll)
			.then(endAll)
			.then(markDestroyed);
	}

	function disconnectAll() {
		return Promise.all(Object.keys(connectedIds).map(disconnect).map(noError));
	}

	function clearAll() {
		return Promise.all(values(pollTimers).map(clearInterval).map(noError));
	}

	function endAll() {
		connected.end();
		disconnected.end();
		scanning.end();
		scanned.end();
		events.end();
		errors.end();
	}

	function noError(promise) {
		return promise.catch(function(e){
			console.error(e);
		});
	}

	function markDestroyed() {
		destroyed = true;
	}

	function on(characteristic) {
		if (destroyed) return;
		return filter(isType(characteristic), events);
	}

	function scan() {
		if (destroyed) return;
		if (scanning()) return;
		scanning(true);

		window.ble.startScan(
			[],
			scanned,
			pipe(
				errors,
				par(scanning, false)
			)
		);
	}

	function stopScan() {
		if (destroyed) return;
		if (!scanning()) return;
		return new Promise(function (resolve, reject) {
			window.ble.stopScan(
				pipe(resolve, par(scanning, false)),
				reject);
		}).catch(reError("Couldn't stop scanning"));
	}

	function connect(device) {
		if (destroyed) return;
		if (connectedIds[device]) return;

		var successConnect = false;

		return new Promise(function (resolve, reject) {
			var timer = setTimeout(timedOut, ACTION_TIMEOUT);
			window.ble.connect(
				device,
				onSuccess,
				onFail
			);

			function onSuccess(data) {
				clearTimeout(timer);
				resolve(data);
				handleConnected(data);
				successConnect = true;
			}

			function onFail(data) {
				clearTimeout(timer);
				if(successConnect){
					resolve(data);
					handleDisconnected(device);
				} else {
					reject({
						device: device,
						error: data,
						message: data.message
					});
					forceDisconnected(device);
				}
				
			}

			function timedOut() {
				forceDisconnected(device);
				onFail(new Error("Timed out"));
			}

		}).catch(reError("Couldn't connect to device " + device));
	}

	function disconnect(device) {
		if (destroyed) return;
		if (!connectedIds[device]) return;

		return new Promise(function (resolve, reject) {
			window.ble.disconnect(
				device,
				resolve,
				reject
			);
		})
			.then(par(handleDisconnected, device))
			.catch(reError("Error disconnecting" + device));
	}

	function write(service, characteristic, value, device) {
		var service_uuid = CONSTANTS[service];
		var characteristic_uuid = CONSTANTS[characteristic];
		var value_data = CONSTANTS[value] || new Uint8Array(value).buffer;

		return new Promise(function (resolve, reject) {
			var emitTimeout = par(reject, new Error("Write timed out"));
			var timer = setTimeout(emitTimeout, ACTION_TIMEOUT);

			window.ble.write(
				device,
				service_uuid,
				characteristic_uuid,
				value_data,
				handle_written,
				reject
			);

			function handle_written() {
				clearTimeout(timer);
				resolve(device);
			}
		}).catch(handle_write_error).catch(reError("Couldn't write " + characteristic + ": " + value + " to " + device));

		function handle_write_error(error) {
			forceDisconnected(device);
			throw error;
		}
	}

	function makeWriter(service, characteristic, value) {
		return par(write, service, characteristic, value);
	}

	function read(service, characteristic, device) {
		var service_uuid = CONSTANTS[service];
		var characteristic_uuid = CONSTANTS[characteristic];

		return new Promise(function (resolve, reject) {
			var emitTimeout = par(reject, new Error("Read timed out"));
			var timer = setTimeout(emitTimeout, ACTION_TIMEOUT);

			window.ble.read(
				device,
				service_uuid,
				characteristic_uuid,
				handle_read,
				reject
			);

			function handle_read(data) {
				clearTimeout(timer);
				resolve(data);
			}
		}).catch(handle_read_error).catch(reError("Couldn't read " + characteristic + " from " + device));

		function handle_read_error(error) {
			forceDisconnected(device);
			throw error;
		}
	}

	function makeReader(service, characteristic) {
		return par(read, service, characteristic);
	}

	function listen(service, characteristic, device) {
		var service_uuid = CONSTANTS[service];
		var characteristic_uuid = CONSTANTS[characteristic];

		var errorMessage = "Couldn't listen to " + characteristic + " from " + device;

		window.ble.startNotification(
			device,
			service_uuid,
			characteristic_uuid,
			handle,
			reError(errorMessage, true)
		);

		return device;

		function handle(buffer) {
			var data = new Uint8Array(buffer);
			events({
				characteristic: characteristic,
				device: device,
				data: data
			});
		}
	}

	function makeListener(service, characteristic) {
		return par(listen, service, characteristic);
	}

	function poll(time, service, characteristic, device) {
		var reader = par(read, service, characteristic, device);

		var pollId = device + "#" + characteristic + "(" + (service || "") + ")";

		if (pollId in pollTimers) return device;

		pollTimers[pollId] = setInterval(reread, time);

		return device;

		function reread() {
			reader()
				.then(pushchange)
				.catch(reError("Couldn't poll " + characteristic + " from " + device));
		}

		function pushchange(buffer) {
			var data = new Uint8Array(buffer);
			events({
				characteristic: characteristic,
				device: device,
				data: data
			});
		}
	}

	function makePoller(time, characteristic, service) {
		return par(poll, time, characteristic, service);
	}

	function unlisten(service, characteristic, device) {
		var service_uuid = CONSTANTS[service];
		var characteristic_uuid = CONSTANTS[characteristic];

		return new Promise(function (resolve, reject) {
			window.ble.stopNotification(
				device,
				service_uuid,
				characteristic_uuid,
				resolve,
				reject
			);
		}).catch(reError("Couldn't stop listening to " + characteristic + " on " + device));
	}

	function unpoll(service, characteristic, device) {
		var pollId = device + "#" + characteristic + "(" + service + ")";

		if (!(pollId in pollTimers)) return device;

		clearInterval(pollTimers[pollId]);
		delete pollTimers[pollId];

		return device;
	}

	function forceDisconnected(device) {
		disconnect(device);
		errors(new Error("Device " + device + " disconnected"));
	}

	function handleDisconnected(device) {
		if (!connectedIds[device]) return;

		delete connectedIds[device];
		disconnected(device);
	}

	function handleConnected(device) {
		connectedIds[device.id] = true;
		connected(device);
	}

	function reError(prefix, nothrow) {
		return function (e) {
			var originalMessage = (e && e.message) ? e.message : e;
			var error = new Error(prefix + ": " + originalMessage);
			error.original = e;
			errors(error);
			if (!nothrow)
				throw e;
		};
	}
}

function isType(characteristic) {
	return function (event) {
		return event.characteristic === characteristic;
	};
}
